import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#openingFrame !== null) {
      cancelAnimationFrame(this.#openingFrame);
      this.#openingFrame = null;
    }
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  #openingFrame = null;

  #isClosing = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openingFrame !== null || this.#isClosing) return;

    this.#previousScrollY = getScrollTop();
    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openingFrame = requestAnimationFrame(() => {
      this.#openingFrame = null;
      if (!this.isConnected || dialog.open || this.#isClosing) return;
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (this.#openingFrame !== null) {
      cancelAnimationFrame(this.#openingFrame);
      this.#openingFrame = null;
      this.#previouslyFocused = null;
      return;
    }

    if (!dialog.open || this.#isClosing) return;
    this.#isClosing = true;

    try {
      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('keydown', this.#handleKeyDown);

      // Force browser to restart animation by resetting it
      // Temporarily remove any existing animation state
      dialog.style.animation = 'none';

      // Force a reflow
      void dialog.offsetWidth;

      // Now add the closing class and restore animation
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      unlockScroll(dialog);
      scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      dialog.close();
      dialog.classList.remove('dialog-closing');

      this.dispatchEvent(new DialogCloseEvent());

      const focusTarget = this.#previouslyFocused;
      this.#previouslyFocused = null;
      if (focusTarget && document.contains(focusTarget) && focusTarget.getClientRects().length > 0) focusTarget.focus();
    } finally {
      this.#isClosing = false;
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    if (event.isComposing || event.defaultPrevented) return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
